/**
 * PortalShell — the one shell every portal uses (back office, trader,
 * partner, relationship manager).
 *
 *   ┌──────┬──────────────┬───────────────────────────────────────────┐
 *   │ dock │ section      │ header — bearing · search ⌘K · actions    │
 *   │ 68px │ panel 244px  ├───────────────────────────────────────────┤
 *   │ mid- │ items of the │ content, max 1280px                       │
 *   │ night│ chosen       │                                           │
 *   │      │ section      │                                           │
 *   └──────┴──────────────┴───────────────────────────────────────────┘
 *
 * The dock carries one icon per section (Overview, Clients, Money…) under the
 * W-compass symbol and is midnight in both appearances. Choosing a section
 * opens its pages in the panel beside it; the panel can be folded away, which
 * leaves the dock and a full-width page. The section that holds the current
 * route is marked with the logo's green|blue split.
 *
 * Under lg the dock and panel become one drawer behind the header's menu
 * button.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { LifeBuoy, PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { PortalHeader } from "./Topbar";
import { SidebarLink } from "./SidebarLink";
import { CommandPalette } from "./CommandPalette";
import { groupNav, type NavSection } from "./navSections";
import type { NavItem } from "./nav";
import { Brand } from "./Brand";
import { cn } from "@/lib/utils";

const PANEL_KEY = "wachsen:panel-folded";

interface Props {
  nav: NavItem[];
  sections: NavSection[];
  prefetch?: (to: string) => Promise<unknown> | void;
  supportTo?: string;
  railFooter?: ReactNode;
  /** Kept for API compatibility — every portal can fold its panel now. */
  collapsible?: boolean;
}

type Group = ReturnType<typeof groupNav<NavItem>>[number];

/** Does this nav item (or one of its children) own the pathname? Returns the
 *  length of the matching route so the longest match wins. */
function matchLen(item: NavItem, pathname: string): number {
  let best = 0;
  const test = (to?: string) => {
    if (to && (pathname === to || pathname.startsWith(to + "/") || pathname.startsWith(to + "?"))) {
      best = Math.max(best, to.length);
    }
  };
  test(item.to);
  item.children?.forEach((c) => test(c.to));
  return best;
}

export function findActive(groups: Group[], pathname: string) {
  let hit: { group: number; item: NavItem; child?: NavItem; len: number } | null = null;
  groups.forEach((g, gi) =>
    g.items.forEach((item) => {
      const len = matchLen(item, pathname);
      if (len && (!hit || len > hit.len)) {
        const child = item.children?.find((c) => c.to && (pathname === c.to || pathname.startsWith(c.to + "/")));
        hit = { group: gi, item, child, len };
      }
    }),
  );
  return hit as { group: number; item: NavItem; child?: NavItem; len: number } | null;
}

export function PortalShell({ nav, sections, prefetch, supportTo, railFooter }: Props) {
  const { pathname } = useLocation();
  const groups = useMemo(() => groupNav(nav, sections), [nav, sections]);
  const active = findActive(groups, pathname);
  const routeGroup = active?.group ?? 0;

  const [openGroup, setOpenGroup] = useState(routeGroup);
  const [drawer, setDrawer] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [folded, setFolded] = useState(() => {
    try { return window.localStorage.getItem(PANEL_KEY) === "1"; } catch { return false; }
  });

  // Follow the route: navigating elsewhere (palette, a link in a page) opens
  // the section that owns the new page.
  const [lastPath, setLastPath] = useState(pathname);
  if (lastPath !== pathname) {
    setLastPath(pathname);
    setOpenGroup(routeGroup);
    setDrawer(false);
  }

  useEffect(() => {
    try { window.localStorage.setItem(PANEL_KEY, folded ? "1" : "0"); } catch { /* private mode */ }
  }, [folded]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      if (e.key === "Escape") setDrawer(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const pickSection = (gi: number) => {
    const g = groups[gi];
    // A single-page section is a destination, not a menu.
    if (g.items.length === 1 && g.items[0].to && !g.items[0].children?.length) {
      setOpenGroup(gi);
      return;
    }
    if (gi === openGroup && !folded) { setFolded(true); return; }
    setOpenGroup(gi);
    setFolded(false);
  };

  const current = groups[openGroup] ?? groups[0];

  const dock = (
    <div className="flex h-full w-[68px] shrink-0 flex-col items-center bg-dock py-4 text-dock-fg">
      <Link to={groups[0]?.items[0]?.to ?? "/"} className="mb-5 grid h-11 w-11 place-items-center rounded-xl transition-transform duration-popover ease-brand hover:scale-105" aria-label="Home">
        <Brand variant="symbol" className="h-10 w-10" />
      </Link>

      <nav aria-label="Sections" className="flex w-full flex-1 flex-col items-center gap-1.5 overflow-y-auto px-2.5">
        {groups.map((g, gi) => {
          const Icon = g.icon;
          const single = g.items.length === 1 && g.items[0].to && !g.items[0].children?.length;
          const isRoute = gi === routeGroup;
          const isOpen = gi === openGroup && !folded;
          const cls = cn(
            "group relative grid h-11 w-11 shrink-0 place-items-center rounded-xl transition-colors duration-feedback ease-brand",
            isRoute ? "bg-white/[0.08] text-white" : "hover:bg-dock-hover hover:text-white",
            isOpen && !isRoute && "bg-white/[0.05] text-white",
          );
          const inner = (
            <>
              {isRoute && <span aria-hidden className="split-rule-v absolute -left-2.5 top-2 bottom-2 w-[3px] rounded-r-full" />}
              {Icon ? <Icon className="h-[19px] w-[19px]" strokeWidth={1.6} /> : <span className="text-xs font-semibold">{g.label?.[0]}</span>}
              <span className="pointer-events-none absolute left-[calc(100%+12px)] top-1/2 z-50 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-[#18212C] px-2.5 py-1.5 font-display text-[11px] font-semibold tracking-wide text-white shadow-raised group-hover:block lg:group-focus-visible:block">
                {g.label}
              </span>
            </>
          );
          return (
            <div key={g.label ?? gi} className="contents">
              {/* a quiet gap before the administrative / return groups */}
              {(g.label === "Administration" || g.label === "Trader area" || g.label === "Profile") && (
                <span aria-hidden className="my-1.5 h-px w-6 bg-white/10" />
              )}
              {single ? (
                <NavLink to={g.items[0].to!} aria-label={g.label ?? undefined} className={cls} onMouseEnter={() => prefetch?.(g.items[0].to!)}>
                  {inner}
                </NavLink>
              ) : (
                <button type="button" aria-label={g.label ?? undefined} aria-expanded={isOpen} onClick={() => pickSection(gi)} className={cls}>
                  {inner}
                </button>
              )}
            </div>
          );
        })}
      </nav>

      <div className="mt-3 flex flex-col items-center gap-1.5 px-2.5">
        {supportTo && (
          <Link to={supportTo} aria-label="Support" title="Support" className="grid h-11 w-11 place-items-center rounded-xl transition-colors duration-feedback ease-brand hover:bg-dock-hover hover:text-white">
            <LifeBuoy className="h-[19px] w-[19px]" strokeWidth={1.6} />
          </Link>
        )}
        <button
          type="button"
          onClick={() => setFolded((v) => !v)}
          aria-label={folded ? "Show section panel" : "Hide section panel"}
          title={folded ? "Show section panel" : "Hide section panel"}
          className="hidden h-11 w-11 place-items-center rounded-xl transition-colors duration-feedback ease-brand hover:bg-dock-hover hover:text-white lg:grid"
        >
          {folded ? <PanelLeftOpen className="h-[18px] w-[18px]" strokeWidth={1.6} /> : <PanelLeftClose className="h-[18px] w-[18px]" strokeWidth={1.6} />}
        </button>
      </div>
    </div>
  );

  const panel = current && (
    <div className="flex h-full w-[244px] shrink-0 flex-col border-r border-border bg-bg-subtle">
      <div className="px-5 pb-3 pt-6">
        <p className="eyebrow">Section</p>
        <h2 className="mt-1 font-display text-[17px] font-bold text-fg">{current.label}</h2>
        <span aria-hidden className="split-rule mt-3 block h-[2px] w-8 rounded-full" />
      </div>
      <nav aria-label={current.label ?? "Pages"} className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-4">
        {current.items.map((item) => (
          <SidebarLink key={item.label} item={item} prefetch={prefetch} />
        ))}
      </nav>
      {railFooter && <div className="border-t border-border p-3">{railFooter}</div>}
    </div>
  );

  return (
    <div className="min-h-screen bg-bg">
      {/* Desktop: dock + panel, fixed to the viewport */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden lg:flex">
        {dock}
        <div
          className={cn(
            "overflow-hidden transition-[width] duration-panel ease-brand",
            folded ? "w-0" : "w-[244px]",
          )}
        >
          {panel}
        </div>
      </aside>

      {/* Mobile drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
          <div className="absolute inset-0 bg-[#05080B]/60 backdrop-blur-[2px] animate-fade-in" onClick={() => setDrawer(false)} />
          <div className="relative flex h-full w-fit max-w-[calc(100vw-48px)] animate-slide-up shadow-overlay">
            {dock}
            <div className="relative">
              {panel}
              <button
                type="button"
                onClick={() => setDrawer(false)}
                aria-label="Close menu"
                className="absolute right-2 top-2 grid h-9 w-9 place-items-center rounded-control text-fg-muted hover:bg-bg-elevated"
              >
                <X className="h-[18px] w-[18px]" strokeWidth={1.6} />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={cn("min-w-0 transition-[padding] duration-panel ease-brand", folded ? "lg:pl-[68px]" : "lg:pl-[312px]")}>
        <PortalHeader
          onMenuToggle={() => setDrawer(true)}
          onSearch={() => setPaletteOpen(true)}
          section={active ? groups[active.group]?.label ?? null : null}
          page={active ? (active.child?.label ?? active.item.label) : null}
          parent={active?.child ? active.item.label : null}
        />
        <main className="px-4 pb-16 pt-6 sm:px-6 xl:px-10">
          <div key={pathname} className="mx-auto w-full max-w-[1280px] animate-rise-in">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} groups={groups} />
    </div>
  );
}
